import { cn } from '@/lib/utils';

/**
 * NoteAlt Icon Component
 *
 * A customizable note/clipboard icon with edit functionality
 *
 * @example
 * ```tsx
 * <NoteAlt />
 * <NoteAlt className="text-blue-500" size={24} />
 * <NoteAlt fill="currentColor" width={16} height={18} />
 * ```
 */

export interface NoteAltProps extends React.SVGProps<SVGSVGElement> {
  /**
   * Size of the icon (sets both width and height)
   * @default 12
   */
  size?: number;
  /**
   * Custom width (overrides size)
   */
  width?: number;
  /**
   * Custom height (overrides size)
   */
  height?: number;
  /**
   * Fill color for the icon
   * Use "currentColor" to inherit text color from parent
   * @default "#1F3959"
   */
  fill?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
}

export function NoteAlt({
  size = 12,
  width,
  height,
  fill = 'currentColor',
  className,
  ...props
}: NoteAltProps) {
  const w = width ?? size;
  const h = height ?? (width ? (width * 14) / 12 : size * (14 / 12)); // Maintain aspect ratio

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={w}
      height={h}
      viewBox="0 0 12 14"
      fill="none"
      className={cn('inline-block flex-shrink-0', className)}
      aria-hidden="true"
      {...props}
    >
      <path
        d="M10.6667 1.33333H7.88C7.6 0.56 6.86667 0 6 0C5.13333 0 4.4 0.56 4.12 1.33333H1.33333C0.6 1.33333 0 1.93333 0 2.66667V12C0 12.7333 0.6 13.3333 1.33333 13.3333H10.6667C11.4 13.3333 12 12.7333 12 12V2.66667C12 1.93333 11.4 1.33333 10.6667 1.33333ZM6 1.16667C6.27333 1.16667 6.5 1.39333 6.5 1.66667C6.5 1.94 6.27333 2.16667 6 2.16667C5.72667 2.16667 5.5 1.94 5.5 1.66667C5.5 1.39333 5.72667 1.16667 6 1.16667ZM10.6667 12H1.33333V2.66667H10.6667V12Z"
        fill={fill}
      />
      <path
        d="M8.05333 6.68667L6.64 5.27333L2.66667 9.24V10.6667H4.06667L8.05333 6.68667Z"
        fill={fill}
      />
      <path
        d="M9.23333 5.51333C9.36667 5.38 9.36667 5.17333 9.23333 5.04L8.29333 4.1C8.16 3.96667 7.95333 3.96667 7.82 4.1L7.11333 4.80667L8.52667 6.22L9.23333 5.51333Z"
        fill={fill}
      />
    </svg>
  );
}
